import { useRef, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from '../clsx';
import { useAnchorPosition, useEscapeKey, useFocusTrap, useOutsideClick, type Placement } from '../hooks';
import styles from './Popover.module.css';

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  /** The element the popover hangs on (trigger button, field, split button). Not needed for `inline`. */
  anchorRef?: RefObject<HTMLElement | null> | undefined;
  placement?: Placement | undefined;
  /** Gap between anchor and popover in px (the screens use 6 for menus, 10 for the profile menu). */
  offset?: number | undefined;
  width?: number | string | undefined;
  /** `menu` = 6 px padding + `--radius-md` (tools/profile menus, listboxes); `panel` = 14/16 px + `--radius-sheet` (calendar, "Später senden"); `card` = 5/0 + `--radius-card` (card menus); `none`. */
  variant?: 'menu' | 'panel' | 'card' | 'none' | undefined;
  /** Where focus goes when the popover opens. Menus manage their own focus (`none`). */
  initialFocus?: 'first' | 'container' | 'none' | undefined;
  /** Accessible name → the popover is announced as a dialog. Menus set `role="menu"` on their list instead. */
  label?: string | undefined;
  labelledBy?: string | undefined;
  id?: string | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
  /** Positioned element to render into instead of `document.body` (preview frames); the popover is then absolutely positioned inside it. */
  container?: HTMLElement | null | undefined;
  /** Renders the panel in the normal flow (no portal, no positioning, no focus handling) — previews and documentation. */
  inline?: boolean | undefined;
  children: ReactNode;
}

const noAnchor: RefObject<HTMLElement | null> = { current: null };

/** Anchored floating layer (portal, `position: fixed`) that flips at the viewport edge; Esc and outside click close it. */
export function Popover({
  open,
  onClose,
  anchorRef,
  placement = 'bottom-start',
  offset = 6,
  width,
  variant = 'menu',
  initialFocus = 'container',
  label,
  labelledBy,
  id,
  className,
  style,
  container,
  inline = false,
  children,
}: PopoverProps) {
  const floatRef = useRef<HTMLDivElement>(null);
  const floating = open && !inline;
  const anchor = anchorRef ?? noAnchor;
  const position = useAnchorPosition(anchor, floatRef, floating, placement, offset, container);
  useEscapeKey(onClose, floating);
  useOutsideClick([floatRef, anchor], onClose, floating);
  useFocusTrap(floatRef, floating, { initialFocus });

  if (!open) return null;
  const named = Boolean(label ?? labelledBy);
  const panel = (
    <div
      ref={floatRef}
      id={id}
      role={named ? 'dialog' : undefined}
      aria-label={label}
      aria-labelledby={labelledBy}
      tabIndex={inline ? undefined : -1}
      data-placement={inline ? undefined : position.placement}
      className={clsx(
        styles.popover,
        inline && styles.inline,
        variant === 'menu' && styles.menu,
        variant === 'panel' && styles.panel,
        variant === 'card' && styles.card,
        className,
      )}
      style={inline ? { width, ...style } : { ...position.style, width, ...style }}
    >
      {children}
    </div>
  );
  if (inline) return panel;
  return createPortal(panel, container ?? document.body);
}
